import * as React from 'react'

import { Command as CommandPrimitive } from 'cmdk'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { cn } from '@/infra'

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
        className,
      )}
      {...props}
    />
  )
}

function CommandInput({
  className,
  isSearching,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & { isSearching?: boolean }) {
  return (
    <div className="border-input flex items-center border-b px-5" {...{ 'cmdk-input-wrapper': '' }}>
      <CommandPrimitive.Input
        data-slot="command-input-wrapper"
        className={cn(
          'placeholder:text-muted-foreground/70 flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
      <Icon name="search" size={20} className="text-muted-foreground/80 ms-3" />
      <Render.If condition={isSearching}>
        {() => <Icon name="loader" className="text-muted-foreground/80 ml-3 animate-spin" />}
      </Render.If>
    </div>
  )
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('max-h-80 overflow-x-hidden overflow-y-auto', className)}
      {...props}
    />
  )
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return <CommandPrimitive.Empty data-slot="command-empty" className="py-6 text-center text-sm" {...props} />
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        '[&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-2 text-gray-800 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  )
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        'data-[filled=true]:bg-accent data-[filled=true]:text-accent-foreground',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
        'relative flex cursor-default items-center gap-3 rounded-md px-2 py-1.5 text-sm outline-hidden select-none',
        className,
      )}
      {...props}
    />
  )
}

export { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList }
